import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/api";
import { useLang } from "@/i18n";
import { ClientLayout } from "./ClientHome";
import { GraduationCap, Lock, ChevronRight } from "lucide-react";

const CATS = ["tecnica", "recupero", "lifestyle"];

export default function ClientEducation() {
  const { t } = useLang();
  const [data, setData] = useState(null);
  const [cat, setCat] = useState(null);

  useEffect(() => {
    api.get("/client/education").then((r) => setData(r.data)).catch(() => setData({ elite: false, articles: [] }));
  }, []);

  if (!data) return <ClientLayout><p className="text-muted-foreground">{t("common.loading")}</p></ClientLayout>;

  if (!data.elite) {
    return (
      <ClientLayout>
        <div data-testid="education-locked" className="max-w-xl mx-auto w-full">
          <div className="kc-card p-8 text-center border-primary/40">
            <span className="w-14 h-14 rounded-full bg-primary/12 grid place-items-center mx-auto mb-4">
              <Lock className="w-7 h-7 text-primary" />
            </span>
            <h1 className="text-2xl font-black uppercase tracking-tight mb-2">{t("edu.title")}</h1>
            <p className="text-muted-foreground text-sm mb-6">{t("edu.locked")}</p>
            <Link to="/app/packages" data-testid="education-upgrade-cta"
              className="kc-btn-primary inline-flex items-center gap-2 !px-6 !py-2.5 text-sm">
              {t("edu.lockedCta")} <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </ClientLayout>
    );
  }

  const articles = cat ? data.articles.filter((a) => a.category === cat) : data.articles;

  return (
    <ClientLayout>
      <div data-testid="client-education-page" className="max-w-xl mx-auto w-full space-y-5">
        <h1 className="text-3xl font-black uppercase tracking-tight flex items-center gap-2">
          <GraduationCap className="w-7 h-7 text-primary" /> {t("edu.title")}
        </h1>
        <div className="flex gap-2 flex-wrap" data-testid="education-filters">
          <button data-testid="education-filter-all" onClick={() => setCat(null)}
            className={`kc-badge !px-4 !py-2 cursor-pointer ${!cat ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground"}`}>
            {t("edu.all")}
          </button>
          {CATS.map((c) => (
            <button key={c} data-testid={`education-filter-${c}`} onClick={() => setCat(c)}
              className={`kc-badge !px-4 !py-2 cursor-pointer ${cat === c ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground"}`}>
              {t(`edu.${c}`)}
            </button>
          ))}
        </div>
        {articles.length === 0 && <p className="text-muted-foreground text-sm">{t("edu.empty")}</p>}
        <div className="space-y-3" data-testid="education-articles">
          {articles.map((a) => (
            <details key={a._id} className="kc-card px-5 py-4 group" data-testid={`education-article-${a._id}`}>
              <summary className="cursor-pointer list-none [&::-webkit-details-marker]:hidden flex items-center justify-between gap-3">
                <span className="min-w-0">
                  <span className="kc-badge bg-primary/12 text-primary mr-2">{t(`edu.${a.category}`)}</span>
                  <span className="font-bold">{a.title}</span>
                </span>
                <ChevronRight className="w-4 h-4 text-primary shrink-0 transition-transform group-open:rotate-90" />
              </summary>
              <p className="text-sm text-muted-foreground mt-3 whitespace-pre-line leading-relaxed">{a.body}</p>
            </details>
          ))}
        </div>
      </div>
    </ClientLayout>
  );
}
